import type { PasswordSetChallenge, RegexPasswordValidationRule } from "../clients/slashid-api"

export function createRegExpFromRule(rule: RegexPasswordValidationRule): RegExp {
    const flags = rule.pattern_qualifiers?.reduce((acc, flag) => {
        if (flag === "case_insensitive") {
            return acc + "i"
        }
        return acc
    }, "")

    const regexp = new RegExp(rule.pattern, flags)
    return regexp
}

type RegexValidationRule = {
    // future proofing - later we might need a discriminated union
    type: "regex"
    name: RegexPasswordValidationRule["name"]
    regexp: RegExp
    matchType: RegexPasswordValidationRule["match_type"]
}

export type ValidationRule = RegexValidationRule

export function createValidationRules(challenge: PasswordSetChallenge): ValidationRule[] {
    // right now we only support Regex rules
    const regexRules = challenge.options.validation_rules.regular_expressions

    if (!regexRules) {
        return []
    }

    const rules: ValidationRule[] = regexRules.map((rule) => {
        const regexp = createRegExpFromRule(rule)
        return {
            type: "regex",
            name: rule.name,
            regexp,
            matchType: rule.match_type,
        }
    })

    return rules
}

type ValidationResult =
    | {
          valid: true
          failedRules: []
      }
    | { valid: false; failedRules: ValidationRule[] }

export function validate({ password, rules }: { password: string; rules: ValidationRule[] }): ValidationResult {
    const failedRules: ValidationRule[] = []

    rules.forEach((rule) => {
        const match = rule.regexp.test(password)
        if (match && rule.matchType === "must_not_match") {
            failedRules.push(rule)
        } else if (!match && rule.matchType === "must_match") {
            failedRules.push(rule)
        }
    })

    if (failedRules.length === 0) {
        return { valid: true, failedRules: [] }
    }

    return { valid: false, failedRules }
}
